import { useState, useMemo, useEffect } from 'react'
import { useEventApi } from 'hooks/use-event-api/useEventApi'
import { useAuth } from 'pages/auth/hooks/useAuth'
import {
  FrontendEvent,
  IntradoEventLoginResponse,
  EventSpeaker,
  Ticket,
  EventAttendee,
  EventAttendeeStatus
} from 'common/types'
import { useNotification } from 'hooks/use-notification/useNotification'
import { useHistory } from 'react-router'
import { useTicketApi } from 'hooks/use-ticket-api/useTicketApi'
import { useAdminApi } from 'hooks/use-admin-api/useAdminApi'
import moment from 'moment'
import { useLocation } from 'react-router-dom'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { AttendanceType, QuestionAnswer } from 'common/types'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { TicketQuestion } from 'common/types/ticket.types'
import { useLocalStorage } from 'hooks/use-local-storage/useLocalStorage'
import { useUserApi } from 'hooks/use-user-api/useUserApi'
import { goToVFair } from 'common/utils/goToVFair'
import { useSummitPost } from 'pages/content/hooks/useSummitPost'
import { goToBigMarkerEvent } from 'common/utils/goToBigMarkerEvent'
import { wordPressUrl } from 'common/globalConstants'

export const useEvents = (
  feedEvents?: boolean,
  eventId?: string,
  register?: boolean,
  claim?: boolean,
  subTitle?: string,
  campaignId?: string,
  campaignCount?: string,
  campaignTicketType?: string
) => {
  const { backendUser, setForwardingUrl, backendPartner } = useAuth()
  const [isAttending, setIsAttending] = useState(false)
  const [attendee, setAttendee] = useState<EventAttendee>()
  const [events, setEvents] = useState<FrontendEvent[]>()
  const [eventsWithRegistrants, setEventsWithRegistrants] = useState<FrontendEvent[]>()
  const [currentEvent, setCurrentEvent] = useState<FrontendEvent>()
  const [ticketCode, setTicketCode] = useState('')
  const [claimCode, setClaimCode] = useState('')
  const [ticketValid, setTicketValid] = useState(false)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const { notify } = useNotification()
  const { pathname } = useLocation()
  const [filter, setFilter] = useState(pathname?.includes('upcoming') ? '&filter=upcoming' : '&filter=all')
  const [show, setShow] = useState(false)
  const [eventCounts, setEventCounts] = useState(0)
  const [eventPreferences, setEventPreferences] = useState<any>()
  const [ticketQuestions, _setTicketQuestions] = useState<TicketQuestion>()
  const [orderedSpeakers, setOrderedSpeakers] = useState<EventSpeaker[]>()
  const [seatingCapacity, setSeatingCapacity] = useState()
  const [tabIndex, setTabIndex] = useState(pathname?.includes('claim') ? 1 : 0)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const { currentSummit: summitEvent } = useSummitPost('bfutr2023')

  const speakerRoles: string[] = ['Host', 'Keynote', 'Fireside Chat', 'Panelist', 'Moderator']

  const {
    fetchFilteredEvents,
    fetchEvent,
    registerForEvent,
    fetchSummitAttendeeAndTicket,
    confirmEventAttendee,
    loginEngageEvent,
    deleteEvent,
    fetchEventCounts,
    getAvailableSeats,
    fetchSummitBySubTitle,
    updateVFairsUser,
    updateEventAttendee
  } = useEventApi()
  const { fetchTicketByCode, claimTicket } = useTicketApi()
  const history = useHistory()
  const [serverDateToday, setServerDateToday] = useState('')
  const { logEventAnalytics } = useAnalytics()
  const { fetchTodaysDate } = useAdminApi()
  const { isDevice } = useScreenSizes()
  const { getItem, clearItem } = useLocalStorage()
  const { fetchUser } = useUserApi()
  const [currentSummit, setCurrentSummit] = useState<boolean>()
  const [showSummitModal, setShowSummitModal] = useState<boolean>(false)

  const getData = useMemo(
    () => async () => {
      let event_response: FrontendEvent
      if (eventId || subTitle) {
        if (eventId) {
          event_response = await fetchEvent(eventId)
          if (location.pathname.includes('/summit') && event_response?.type === 'Summit' && event_response?.subTitle === 'bfutr2023') {
            window.location.replace(wordPressUrl)
            return
          }
        } else if (subTitle) {
          event_response = await fetchSummitBySubTitle(subTitle)
        } else {
          return
        }

        if (event_response) {
          setCurrentEvent(event_response)
          setShow(register)
          if (event_response.speakers) {
            setOrderedSpeakers([
              ...event_response.speakers?.filter(x => x.role === 'Keynote'),
              ...event_response.speakers?.filter(x => x.role === 'Fireside Chat'),
              ...event_response.speakers?.filter(x => x.role === 'Panelist'),
              ...event_response.speakers?.filter(x => x.role === 'Moderator'),
              ...event_response.speakers?.filter(x => x.role === 'Host'),
              ...event_response.speakers?.filter(x => !speakerRoles.includes(x.role))
            ])
          }
          if (event_response.type === 'Summit' && event_response.subTitle === 'bftur2022') {
            setCurrentSummit(true)
            return
          }
        } else {
          notify('error', 'Unknown Event Id')
          history.push('/events')
        }
      }
    },
    [eventId]
  )

  const getTodaysDateFromServer = async () => {
    const response = await fetchTodaysDate()
    if (response) {
      const serverDate = moment(response).format('YYYY-MM-DD')
      setServerDateToday(serverDate)
    } else {
      notify('error', 'Unable to Get Server Date')
    }
  }

  const setTicketQuestions = (questions?: TicketQuestion) => {
    if (questions) {
      _setTicketQuestions(questions)
    } else {
      const raceQuestion = backendUser?.onboardingQuestions?.find(
        x => x.question === 'Do You Identify As Being Part Of The Black Community'
      )?.answer
      _setTicketQuestions({
        race: raceQuestion != '' ? raceQuestion : 'No',
        ticketType: 'general',
        reason: ''
      })
    }
  }

  const getSeatingData = async () => {
    const response = await getAvailableSeats(eventId)
    if (response) setSeatingCapacity(response)
  }

  useEffect(() => {
    if (backendUser && !backendUser?.isOnBoarded) {
      history.push('/settings')
      return
    }
    if (backendUser && pathname?.includes('summit') && pathname?.includes('register')) {
      isDevice ? history.push(`/summit/${eventId}/bfutr/registration`) : setShowTicketModal(true)
      if (claim) setTabIndex(1)
    }
    getTodaysDateFromServer()

    getData()

    if (eventId) {
      getSeatingData()
    }

    const getEvents = async () => {
      const response = feedEvents ? await fetchFilteredEvents('&filter=live', 1, 3) : await fetchFilteredEvents(filter)
      const filterEvents = response?.data?.filter(event =>
        event?.status != 'draft' && event?.status != null && event?.status != 'scheduled' ? event : null
      )
      setEvents(filterEvents)
    }
    const getEventCounts = async () => {
      const getAcceptedConnectionCounts = await fetchEventCounts()
      setEventCounts(getAcceptedConnectionCounts)
    }
    getEventCounts()
    getEvents()
  }, [filter, pathname, backendUser])

  const verifyTicketCode = async () => {
    const ticket = await fetchTicketByCode(ticketCode)
    if (ticket) {
      if (ticket?.ownerId) {
        notify('error', 'Ticket already has an Owner')
        return
      }
      setTicketValid(true)
      const partnerLog = {}
      const userLog = {}
      userLog['User ID'] = backendUser?.id
      if (backendUser?.partnerId) {
        partnerLog['Partner ID'] = backendPartner?.id
        partnerLog['Company Name'] = backendPartner?.name
        partnerLog['Account Type'] = 'Partner'
      } else {
        userLog['Account Type'] = backendUser?.role
      }
      logEventAnalytics(
        backendUser?.partnerId ? 'Partner' : backendUser?.role,
        'Event Claim Pass',
        'both',
        'Event Claim Pass',
        {
          'Event name': currentEvent?.title,
          'Date of event': currentEvent?.date,
          ...partnerLog,
          ...userLog
        },
        backendUser?.id,
        {
          $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
          $email: backendUser?.username
        }
      )
    } else {
      notify('error', 'Invalid Ticket')
    }
  }

  const claimTicketCode = async (callback?: () => void) => {
    const response = await claimTicket(backendUser?.id, currentEvent?.id, ticketCode, claimCode)
    if (response.error) {
      notify('error', response?.message ?? 'Unable to Claim Ticket')
      return
    }
    notify('success', 'Ticket Claimed')
    setIsAttending(true)
    setTicketValid(false)
    setTicketCode('')
    setClaimCode('')
    if (callback) callback()
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = backendUser?.id
    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = backendUser?.role
    }
    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Event Claim Pass',
      'both',
      'Event Claim Pass',
      {
        'Event name': currentEvent?.title,
        'Date of event': currentEvent?.date,
        ...partnerLog,
        ...userLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
    // logEventAnalytics(
    //   'Member',
    //   `Event claim pass`,
    //   'both',
    //   'Claim pass',
    //   { Email: backendUser?.username, 'Event name': currentEvent?.title, 'Date of event': currentEvent?.date },
    //   backendUser?.id,
    //   {
    //     $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
    //     $email: backendUser?.username
    //   }
    // )
  }

  const purchaseCampaignTicket = async (eventId: string) => {
    const response = await registerForEvent(backendUser?.id, eventId, {
      numOfTickets: 1,
      attendeeType: campaignTicketType,
      purchaseType: 'individual',
      cost: campaignTicketType == 'vip' ? '370.76' : '',
      paymentRef: 'Campaign_' + campaignId,
      preferences: { reasonForAttending: 'jobs' },
      campaignId: campaignId,
      campaignCount: campaignCount
    })

    if (response) {
      const partnerLog = {}
      const userLog = {}
      userLog['User ID'] = backendUser?.id
      if (backendUser?.partnerId) {
        partnerLog['Partner ID'] = backendPartner?.id
        partnerLog['Company Name'] = backendPartner?.name
        partnerLog['Account Type'] = 'Partner'
      } else {
        userLog['Account Type'] = backendUser?.role
      }
      logEventAnalytics(
        backendUser?.partnerId ? 'Partner' : backendUser?.role,
        'Event registered',
        'both',
        'Event registration',
        {
          'Event ID': currentEvent?.id,
          'Event name': currentEvent?.title,
          Email: backendUser?.username,
          'Date of event': currentEvent?.date,
          Type: currentEvent?.type,
          ...partnerLog,
          ...userLog
        },
        backendUser?.id,
        {
          $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
          $email: backendUser?.username
        }
      )
      setShowSuccessModal(true)
      setIsAttending(true)
    }
  }

  const purchaseTicket = async (e?: any, callback?: () => void) => {
    const ref_array = e?.paymentRef?.split('_') as string[]
    const response = await registerForEvent(backendUser?.id, currentEvent?.id, {
      numOfTickets: e?.quantity,
      attendeeType: e?.event,
      purchaseType: e?.selfPurchase ? 'individual' : 'bulk',
      cost: e?.total,
      paymentRef: ref_array && ref_array?.length > 1 ? `${ref_array[0]}_${ref_array[1]}` : '',
      preferences: e?.preferences
    })
    notify(response ? 'success' : 'error', response ? 'Ticket(s) Purchased' : 'Unable to Purchase Ticket')
    if (response) {
      logEventAnalytics(
        'Member',
        'Event registered',
        'both',
        'Event registration',
        {
          Email: backendUser?.username,
          'Event name': currentEvent?.title,
          'Date of event': currentEvent?.date,
          Type: currentEvent?.type,
          'Account type': backendUser?.role
        },
        backendUser?.id,
        {
          $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
          $email: backendUser?.username
        }
      )

      const referrer = getItem('event-referrer')
      if (referrer) {
        const referrerDetails = await fetchUser(referrer)

        logEventAnalytics(
          backendUser?.partnerId ? 'Partner' : backendUser?.role,
          'Event invite accepted',
          'both',
          'Member registers for event from link shared',
          {
            'Event ID': currentEvent.id,
            'Event name': currentEvent?.title,
            'User ID': backendUser.id,
            'Referrer email': referrerDetails.username,
            'Account type': backendUser?.role
          },
          backendUser?.id,
          {
            $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
            $email: backendUser?.username
          }
        )
        clearItem('event-referrer')
      }
    }
    if (e?.selfPurchase) {
      setIsAttending(true)
    }
    if (callback) callback()
  }

  const registerForMasterclass = async (preferences: any, attendanceType: AttendanceType) => {
    const response = await registerForEvent(backendUser?.id, currentEvent?.id, {
      numOfTickets: 1,
      attendeeType: 'general',
      purchaseType: 'individual',
      cost: '0',
      preferences: preferences,
      attendance: attendanceType
    })
    setAttendee(await confirmEventAttendee(backendUser.id, eventId))
    notify(response ? 'success' : 'error', response ? 'Registration complete' : 'Unable to register, try again')
    if (response) {
      setIsAttending(true)
      const partnerLog = {}
      const userLog = {}
      userLog['User ID'] = backendUser?.id
      if (backendUser?.partnerId) {
        partnerLog['Partner ID'] = backendPartner?.id
        partnerLog['Company Name'] = backendPartner?.name
        partnerLog['Account Type'] = 'Partner'
      } else {
        userLog['Account Type'] = backendUser?.role
      }
      logEventAnalytics(
        backendUser?.partnerId ? 'Partner' : backendUser?.role,
        'Event registered',
        'both',
        'Event registration',
        {
          Email: backendUser?.username,
          'Event name': currentEvent?.title,
          'Date of event': currentEvent?.date,
          Type: currentEvent?.type,
          'Event ID': currentEvent.id,
          ...partnerLog,
          ...userLog
        },
        backendUser?.id,
        {
          $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
          $email: backendUser?.username
        }
      )
    }
  }

  const joinEngageEvent = async (userId: string, eventId: string, showKey: string) => {
    const response: IntradoEventLoginResponse = await loginEngageEvent(userId, eventId, showKey)
    if (response?.loginKey) {
      if (isDevice)
        return window.location.assign(
          `https://onlinexperiences.com/scripts/Server.nxp?LASCmd=AI:4;F:APIUTILS!50505&LoginTicketKey=${response.loginKey}`
        )
      const engageLink = document.createElement('a')
      engageLink.setAttribute(
        'href',
        `https://onlinexperiences.com/scripts/Server.nxp?LASCmd=AI:4;F:APIUTILS!50505&LoginTicketKey=${response.loginKey}`
      )
      engageLink.setAttribute('target', '_blank')
      engageLink.click()
    }
  }

  const joinVFairsEvent = async () => {
    const attendeeResponse: {
      attending: boolean
      ticket: Ticket
      eventAttendee: EventAttendee
    } = await fetchSummitAttendeeAndTicket(backendUser?.id, eventId)
    if (attendeeResponse?.eventAttendee?.metaData?.loginUrl) {
      await updateVFairsUser(attendeeResponse?.eventAttendee?.attendeeId, eventId)
      if (isDevice) return window.location.assign(attendeeResponse?.eventAttendee?.metaData?.loginUrl)
      goToVFair(attendeeResponse)
    }
  }

  const joinBigMarkerEvent = async () => {
    if (backendUser && attendee) {
      goToBigMarkerEvent(attendee)
    }
  }

  useEffect(() => {
    const getData = async () => {
      if (eventId && backendUser) {
        const attendee = await confirmEventAttendee(backendUser.id, eventId)
        const attending = !!attendee
        setAttendee(attendee)
        setIsAttending(attending)
      }
      if (campaignId && backendUser && backendUser.isOnBoarded && backendUser.isOnBoarded && eventId) {
        await purchaseCampaignTicket(eventId)
      }
    }

    getData()
    setTicketQuestions()
  }, [backendUser])

  const checkIn = async (attendeeId: string, eventId: string) => {
    const response: EventAttendee = await updateEventAttendee(attendeeId, eventId, {
      status: EventAttendeeStatus.ATTENDED
    })

    if (response) {
      setAttendee(response)
    }
  }

  const checkUserProfile = (isSummit?: boolean) => {
    let userMandatoryQuestionAnswers: QuestionAnswer[] = []
    if (isSummit) {
      backendUser?.onboardingQuestions?.map((questionObj, i) => {
        switch (questionObj.question) {
          case 'First Name': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Last Name': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Years of experience': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Industry': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Experience Level': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Area of Expertise': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
        }
      })
    } else {
      backendUser?.onboardingQuestions?.map((questionObj, i) => {
        switch (questionObj.question) {
          case 'First Name': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Last Name': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Age Range': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Location': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Experience Level': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
          case 'Area of Expertise': {
            return userMandatoryQuestionAnswers.push(questionObj)
          }
        }
      })
    }
    if (userMandatoryQuestionAnswers.length == 6) {
      return userMandatoryQuestionAnswers.every(qa => qa?.answer?.length > 0)
    } else {
      return false
    }
  }

  const getEvent = async (eventId: string) => {
    const event_response = await fetchEvent(eventId)
    return event_response
  }

  const remindUserAboutSummit = async () => {
    const summitAttendee = await fetchSummitAttendeeAndTicket(backendUser?.id, summitEvent?.id)
    if (!summitAttendee?.ticket) {
      isDevice ? history.push('/reminder/summit') : setShowSummitModal(true)
    }
    return
  }

  return {
    attendee,
    checkIn,
    getEvent,
    filter,
    setFilter,
    backendUser,
    setForwardingUrl,
    events,
    setEvents,
    show,
    setShow,
    currentEvent,
    setCurrentEvent,
    ticketCode,
    setTicketCode,
    ticketQuestions,
    setTicketQuestions,
    ticketValid,
    verifyTicketCode,
    claimTicketCode,
    claimCode,
    setClaimCode,
    purchaseTicket,
    isAttending,
    fetchSummitAttendeeAndTicket,
    serverDateToday,
    showTicketModal,
    setShowTicketModal,
    confirmEventAttendee,
    joinEngageEvent,
    deleteEvent,
    eventsWithRegistrants,
    setEventsWithRegistrants,
    eventCounts,
    registerForMasterclass,
    checkUserProfile,
    eventPreferences,
    setEventPreferences,
    setIsAttending,
    backendPartner,
    orderedSpeakers,
    tabIndex,
    setTabIndex,
    seatingCapacity,
    setSeatingCapacity,
    getSeatingData,
    joinVFairsEvent,
    currentSummit,
    setShowSuccessModal,
    showSuccessModal,
    showSummitModal,
    setShowSummitModal,
    summitEvent,
    joinBigMarkerEvent
  } as const
}
